import { Capacitor, registerPlugin } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

/**
 * The iOS app is a native shell around this same site. These are the few
 * places where an app needs something a browser does not.
 */
export const isNative = Capacitor.isNativePlatform();

const Print = registerPlugin<{ print(opts?: { name?: string }): Promise<{ completed: boolean }> }>('Print');

/** "Save as PDF": the browser's print dialog, or the iOS print sheet in the app. */
export function printPage(name?: string): void {
  if (isNative) void Print.print({ name }).catch(() => undefined);
  else window.print();
}

/**
 * In the app, a link meant for a new tab (the survey, a PDF) opens in an
 * in-app browser with a Done button; otherwise it would replace the app's
 * only screen with no way back.
 */
export function installNativeLinks(): void {
  if (!isNative) return;
  document.documentElement.classList.add('rb-native');
  document.addEventListener(
    'click',
    (e) => {
      const a = (e.target as Element | null)?.closest?.('a[target="_blank"]') as HTMLAnchorElement | null;
      if (!a?.href) return;
      e.preventDefault();
      void Browser.open({ url: a.href, presentationStyle: 'popover' });
    },
    true,
  );
}

/** Open a link outside the app: the in-app browser on a phone, a new tab on the web. */
export function openExternal(href: string): void {
  if (isNative) void Browser.open({ url: new URL(href, location.href).href, presentationStyle: 'popover' });
  else window.open(href, '_blank', 'noopener');
}

// ── Buying a report in the iOS app (Apple in-app purchase) ─────────────────

export const REPORT_PRODUCT = 'ai.silentfoundry.readybuy.report';

type StoreTransaction = { jws: string; transactionId: string };
type PurchasesPlugin = {
  product(opts: { id: string }): Promise<{ id: string; displayPrice: string; title: string }>;
  purchase(opts: { id: string; account: string }): Promise<{ status: 'purchased' | 'cancelled' | 'pending'; jws?: string; transactionId?: string }>;
  unfinished(): Promise<{ transactions: StoreTransaction[] }>;
  finish(opts: { transactionId: string }): Promise<void>;
  addListener(event: 'transaction', fn: (t: StoreTransaction) => void): Promise<{ remove(): Promise<void> }>;
};
const Purchases = registerPlugin<PurchasesPlugin>('Purchases');

/** The report's price as the App Store shows it in the buyer's currency, or null off iOS. */
export async function reportPrice(): Promise<string | null> {
  if (!isNative) return null;
  return Purchases.product({ id: REPORT_PRODUCT })
    .then((p) => p.displayPrice)
    .catch(() => null);
}

/**
 * Hand a transaction to the server and finish it only once credited. Left
 * unfinished, StoreKit offers it again on the next launch, so a dropped
 * connection never loses a paid report.
 */
async function credit<T>(t: StoreTransaction, send: (jws: string) => Promise<T>): Promise<T> {
  const out = await send(t.jws);
  await Purchases.finish({ transactionId: t.transactionId }).catch(() => undefined);
  return out;
}

export type BuyResult<T> = { status: 'purchased'; result: T } | { status: 'cancelled' | 'pending' };

/** Show Apple's purchase sheet for one report, tagged to this account. */
export async function buyReport<T>(account: string, send: (jws: string) => Promise<T>): Promise<BuyResult<T>> {
  const r = await Purchases.purchase({ id: REPORT_PRODUCT, account });
  if (r.status !== 'purchased' || !r.jws || !r.transactionId) return { status: r.status === 'purchased' ? 'cancelled' : r.status };
  return { status: 'purchased', result: await credit({ jws: r.jws, transactionId: r.transactionId }, send) };
}

/**
 * On launch, credit anything bought but not yet credited, and keep listening
 * for purchases that complete later (Ask to Buy, an interrupted sheet).
 */
export function syncPurchases<T>(send: (jws: string) => Promise<T>, onCredited: (r: T) => void): () => void {
  if (!isNative) return () => undefined;
  let stopped = false;
  const handle = (t: StoreTransaction) =>
    credit(t, send)
      .then((r) => !stopped && onCredited(r))
      .catch(() => undefined);
  void Purchases.unfinished()
    .then(({ transactions }) => transactions.forEach(handle))
    .catch(() => undefined);
  const sub = Purchases.addListener('transaction', handle);
  return () => {
    stopped = true;
    void sub.then((s) => s.remove()).catch(() => undefined);
  };
}
